import { Camera, Loader2, X } from "lucide-react";
import { type ChangeEvent, useRef, useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { useFileUpload } from "@/hooks/use-file-upload";
import { useRemovePersonAvatar, useSetPersonAvatar } from "@/hooks/use-persons";
import { cn } from "@/lib/utils";
import { AvatarCropDialog } from "./AvatarCropDialog";
import { PersonAvatar } from "./PersonAvatar";

interface PersonAvatarEditableProps {
  /**
   * Minimal person shape — the control only needs the id (for the mutations),
   * the name (initials fallback) and the current presigned avatarUrl (display).
   */
  person: { id: string; name: string; avatarUrl: string | null };
}

/**
 * Editable profile picture: the xl {@link PersonAvatar} with a camera button to
 * upload/replace and an X button to remove. Used inside the admin-only person
 * edit form. The photo applies immediately via useSetPersonAvatar — it does not
 * go through the form's effective-dated save.
 */
export function PersonAvatarEditable({ person }: PersonAvatarEditableProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { uploadFile, isUploading } = useFileUpload();
  const setAvatar = useSetPersonAvatar(person.id);
  const removeAvatar = useRemovePersonAvatar(person.id);
  // The picked file awaiting crop confirmation; the crop dialog is open while set.
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const busy = isUploading || setAvatar.isPending || removeAvatar.isPending;
  const hasPhoto = Boolean(person.avatarUrl);

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset so picking the same file again still fires onChange.
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setPendingFile(file);
  }

  async function onCropped(cropped: File) {
    setPendingFile(null);
    const result = await uploadFile(cropped);
    if (result) setAvatar.mutate(result.fileId);
  }

  return (
    <div className="relative size-20 shrink-0">
      <PersonAvatar name={person.name} seed={person.id} size="xl" imageUrl={person.avatarUrl} />

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onPick}
        disabled={busy}
      />

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        aria-label={hasPhoto ? "Replace profile picture" : "Upload profile picture"}
        title={hasPhoto ? "Replace profile picture" : "Upload profile picture"}
        className={cn(
          "absolute -right-1.5 -bottom-1.5 flex size-7 items-center justify-center rounded-full",
          "border-2 border-card bg-primary text-primary-foreground shadow-sm transition-colors",
          "hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "disabled:pointer-events-none disabled:opacity-60",
        )}
      >
        {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Camera className="size-3.5" />}
      </button>

      {hasPhoto && (
        /* Guarded: the photo and its stored file are deleted outright, from a
           6px X sitting next to the avatar. */
        <ConfirmDialog
          title="Remove this profile picture?"
          description="The photo is deleted. A new one would have to be uploaded and cropped again."
          confirmLabel="Remove photo"
          destructive
          onConfirm={() => removeAvatar.mutate()}
          trigger={
            <button
              type="button"
              disabled={busy}
              aria-label="Remove profile picture"
              title="Remove profile picture"
              className={cn(
                "absolute -top-1.5 -right-1.5 flex size-6 items-center justify-center rounded-full",
                "border-2 border-card bg-muted text-muted-foreground shadow-sm transition-colors",
                "hover:bg-danger-bg hover:text-danger-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "disabled:pointer-events-none disabled:opacity-60",
              )}
            >
              <X className="size-3" />
            </button>
          }
        />
      )}

      <AvatarCropDialog
        file={pendingFile}
        onCancel={() => setPendingFile(null)}
        onSave={(cropped) => void onCropped(cropped)}
      />
    </div>
  );
}
